/**
 * @fileoverview Main entry point for the CasebookMap application.
 * Initializes the MapApp, sidebar UI, and global event handlers.
 * This project is by Jesse Reichler (jessereichler@gmail.com)
 *
 * @module main
 */

import { MapApp } from './app/MapApp.js';
import { VERSION_INFO } from './constants.js';
import { setTrustedAppDataRoots, isTrustedAppDataUrl, describeTrustedAppDataRoots, resolveDirectoryUrl, normalizeStaticBasePath, showStatusWarning } from './utils/jrfuncs.js';
import { isValidKeyStoreName, KEYSTORE_NAME_MAX_LENGTH } from './state/StateManager.js';
import { getMapPageConfig } from './config/pageConfig.js';









// ==========================================
// MAIN APP ENTRY POINT
// ==========================================


/**
 * Main application entry point.
 * Creates the MapApp instance, loads configuration, initializes UI components,
 * and sets up the map.
 * @async
 * @throws {Error} If initialization fails, displays an alert and logs to console
 */
async function main() {
    try {
        console.info(`CasebookMap ${VERSION_INFO.CbmVersionString} [${VERSION_INFO.CbmVersionDate}] by ${VERSION_INFO.CbmContact} initializing...`);

        // Where to load configuration from
        const {configPath, staticBasePath, appKeyStore, worldPath, worldId, pdfUrl, refusedParamMessages} = await getConfigurationAndStaticPaths();

        // Create map application
        const mapApp = new MapApp('map-container', { staticBasePath, appKeyStore, worldPath, worldId, pdfUrl });

        // tell the user about url parameters we ignored (they would otherwise silently get the default)
        for (const message of refusedParamMessages) {
            mapApp.addWarning(message);
            showStatusWarning(message);
        }

        // Initialize map with configuration
        await mapApp.initialize(configPath);

        // Remove the one-shot startup params from the address bar now that they've been consumed,
        // so reloading/bookmarking the page doesn't re-apply them.
        stripStartupUrlParams();

        // all initialized
        console.info('CasebookMap initialized and ready.');

    } catch (error) {
        console.error('ERROR: Failed to initialize Casebook Map App:', error);
        alert('Failed to initialize Casebook Map App. Check console for details.');
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', main);
} else {
    main();
}



























// ==========================================
// INITIALIZATION AND SETUP
// ==========================================








// ==========================================
// CONFIGURATION LOADING
// ==========================================


/**
 * Determines the configuration file path from URL query parameters.
 * Falls back to 'data/appConfig.json' if no config parameter is provided.
 * @returns {Promise<string>} Path to the configuration JSON file
 * @example
 * // URL: http://localhost:8000/index.html?config=configSample.json
 * const path = await getConfigurationPath(); // Returns 'configSample.json'
 * @example
 * // URL: http://localhost:8000/index.html (no config param)
 * const path = await getConfigurationPath(); // Returns 'data/appConfig.json'
 * @private
 */
async function getConfigurationAndStaticPaths() {
    const urlParams = new URLSearchParams(window.location.search);
    const refusedParamMessages = [];

    // The app parses (and renders parts of) its config, data and help html, so it may read them only
    // from its OWN folders: the static base and the world-data tree. Same-origin is not enough --
    // case authors' uploads are served from this origin too (/media/cases/...). The roots come from
    // the page template (the #mapAppConfig element's data- attributes, see config/pageConfig.js);
    // the url params below must point inside them, and so must every url in a loaded config
    // (loadJsonDataFromPath enforces the same rule on every fetch).
    const pageConfig = getMapPageConfig();
    const defaultStaticBasePath = normalizeStaticBasePath(pageConfig.staticPath);
    const worldDataRoot = pageConfig.worldDataRoot;
    setTrustedAppDataRoots([defaultStaticBasePath, worldDataRoot]);

    const configPath = getTrustedDataUrlParam(urlParams, 'config', false, refusedParamMessages) || pageConfig.configPath;
    // static is a folder that files are joined onto, so it is normalized to a clean directory path
    const staticParam = getTrustedDataUrlParam(urlParams, 'static', true, refusedParamMessages);
    const staticBasePath = staticParam ? normalizeStaticBasePath(staticParam) : defaultStaticBasePath;
    const appKeyStore = getKeyStoreParam(urlParams, refusedParamMessages) || pageConfig.keyStore;
    const worldPath = getTrustedDataUrlParam(urlParams, 'worldPath', false, refusedParamMessages) || null;
    const worldId = urlParams.get('worldId') || null;
    // a case pdf may legitimately live on another site, so any http(s) url is accepted here
    const pdfUrl = getHttpUrlParam(urlParams, 'pdfUrl') || null;

    return {configPath, staticBasePath, appKeyStore, worldPath, worldId, pdfUrl, refusedParamMessages};
}


/**
 * Returns a url query parameter only if it points inside the app's own data folders (the static
 * base or the world-data tree, see setTrustedAppDataRoots); otherwise warns on the console, records
 * a message for the user and returns null.
 * @param {URLSearchParams} urlParams - The page's query parameters
 * @param {string} name - Parameter name
 * @param {boolean} isDirectory - True if the value names a folder (checked as that folder)
 * @param {string[]} refusedParamMessages - Collects a message for each refused parameter
 * @returns {string|null} The parameter value, or null if absent or outside the app's folders
 * @private
 */
function getTrustedDataUrlParam(urlParams, name, isDirectory, refusedParamMessages) {
    const value = urlParams.get(name);
    if (!value) {
        return null;
    }
    let urlToCheck = value;
    if (isDirectory) {
        const dirUrl = resolveDirectoryUrl(value);
        urlToCheck = (dirUrl && !value.includes('\\')) ? dirUrl.href : null;
        // a folder value must not carry a query string or fragment (they would be dropped silently)
        if (/[?#]/.test(value)) {
            urlToCheck = null;
        }
    }
    if (urlToCheck && isTrustedAppDataUrl(urlToCheck)) {
        return value;
    }
    const message = `Ignored the ?${name}= parameter "${value}": the map only reads its data from ${describeTrustedAppDataRoots()}.`;
    console.warn(`CasebookMap: ${message}`);
    refusedParamMessages.push(message);
    return null;
}


/**
 * Returns the ?keystore= parameter (a separate set of saved state, e.g. one per case) only if it
 * fits the storage-key rule a settings backup restore accepts (StateManager.isValidKeyStoreName);
 * otherwise warns on the console, records a message for the user and returns null, so the map
 * uses its default store. A name outside that rule (a space, "café", "x:y", "_lead", "a__b") used to
 * be accepted and wrote keys no backup of that store could restore.
 * @param {URLSearchParams} urlParams - The page's query parameters
 * @param {string[]} refusedParamMessages - Collects a message for a refused parameter
 * @returns {string|null} The keystore name, or null if absent or refused
 * @private
 */
function getKeyStoreParam(urlParams, refusedParamMessages) {
    const value = urlParams.get('keystore');
    if (!value) {
        return null;
    }
    if (isValidKeyStoreName(value)) {
        return value;
    }
    const shownValue = (value.length > 80) ? value.slice(0, 80) + '...' : value;
    const message = `Ignored the ?keystore= parameter "${shownValue}": a keystore name may use only letters, digits, "." and "-", joined by single "_" (at most ${KEYSTORE_NAME_MAX_LENGTH} characters), so the map is using its default store.`;
    console.warn(`CasebookMap: ${message}`);
    refusedParamMessages.push(message);
    return null;
}


/**
 * Returns a url query parameter only if it is an http(s) url (absolute, or relative to this page);
 * otherwise warns on the console and returns null.
 * @param {URLSearchParams} urlParams - The page's query parameters
 * @param {string} name - Parameter name
 * @returns {string|null} The parameter value, or null if absent or not http(s)
 * @private
 */
function getHttpUrlParam(urlParams, name) {
    const value = urlParams.get(name);
    if (!value) {
        return null;
    }
    let resolved = null;
    try {
        resolved = new URL(value, window.location.href);
    } catch (e) {
        resolved = null;
    }
    if (resolved && (resolved.protocol === 'http:' || resolved.protocol === 'https:')) {
        return value;
    }
    console.warn(`CasebookMap: ignoring the ?${name}= parameter "${value}": only http(s) urls are accepted.`);
    return null;
}


/**
 * Removes the one-shot startup parameters (worldPath, worldId, pdfUrl) from the browser
 * address bar after they've been consumed, without reloading the page. Any other query
 * params (config, static, keystore) and the URL hash are preserved.
 * @private
 */
function stripStartupUrlParams() {
    const url = new URL(window.location.href);
    const startupParams = ['worldPath', 'worldId', 'pdfUrl'];

    let changed = false;
    for (const param of startupParams) {
        if (url.searchParams.has(param)) {
            url.searchParams.delete(param);
            changed = true;
        }
    }

    if (changed) {
        // replaceState (not pushState) so the cleaned URL doesn't add a back-button history entry
        history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
}


